import { tKey } from 'ember-intl';

export const DEFAULT_DAYS_SHOWN_IN_OD = 30;

export const OD_DEFAULT_DATE_FORMAT = 'll';

export const OD_FB_FILTERS = {
  DAY: 'day',
};

export const OD_SETTINGS_OPTION_KEYS = {
  SETTINGS_MODAL: 'od-settings-modal',
};

export const OD_REF_DAYS_AGO_VALUES = {
  yesterday: 1,
  threeDaysAgo: 3,
  sevenDaysAgo: 7,
  fourteenDaysAgo: 14,
};

export const OD_SELECTED_TILES_IDS = {
  occupancy: 'occupancy', // This tile ID is added for FE only for the sole purpose of usage tracking. The actual contents of the occupancy card are still based on my_otb/market_otb
  my_otb: 'my_otb',
  market_otb: 'market_otb',
  my_revenue: 'my_revenue',
  my_adr: 'my_adr',
  demand_level: 'demand_level',
  top_searching_countries: 'top_searching_countries',
  pricing_difference: 'pricing_difference',
  price_opportunity: 'price_opportunity',
  my_parity: 'my_parity',
  events: 'events',
  triptease_monthlybudget: 'triptease_monthlybudget',
  triptease_bookingfee: 'triptease_bookingfee',
  triptease_revenueshare: 'triptease_revshare',
  triptease_roas: 'triptease_roas',
  triptease_impressionshare: 'triptease_impressionshare',
  ranking: 'ranking',
  distribution: 'distribution', // This tile ID is added for FE only for the sole purpose of usage tracking of distribution health tiles. The actual contents of the occupancy card are still based on ranking/my_parity
  stay_date_graph: 'stay_date_graph', // aka Daily patterns
  short_term_rental: 'short_term_rental',
};

export type SupportedOverviewDashboardTileIds =
  keyof typeof OD_SELECTED_TILES_IDS;

// Keys that can be used to retrieve data from OD API responses
export const OD_API_DATA_KEYS = {
  my_otb_percentage: 'my_otb_percentage',
  my_otb_percentage_pickup: 'my_otb_percentage_pickup',
  my_otb_roomnights: 'my_otb_roomnights',
  my_otb_roomnights_pickup: 'my_otb_roomnights_pickup',
  market_otb_percentage: 'market_otb_percentage',
  market_otb_percentage_pickup: 'market_otb_percentage_pickup',
  revenue: 'revenue',
  revenue_diff: 'revenue.diff',
  adr: 'adr',
  adr_diff: 'adr.diff',
  mi_demand_level: 'mi_demand_level',
  mi_demand_level_diff: 'mi_demand_level.diff',
  comp_pricing_diff: 'comp_pricing_diff', // aka price difference
  rental_otb: 'rental_otb',
  rental_otb_diff: 'rental_otb.diff',
  rental_rate: 'rental_rate',
};

// Keys used to identify settings in the overviewSettings object
export const OD_SETTINGS_KEYS = {
  adr_pickup_opt_in: 'adr_pickup_opt_in',
  average_demand_level_opt_in: 'average_demand_level_opt_in',
  compset_change_opt_in: 'compset_change_opt_in',
  compset_soldout_opt_in: 'compset_soldout_opt_in',
  market_otb_pickup_opt_in: 'market_otb_pickup_opt_in',
  otb_pickup_opt_in: 'otb_pickup_opt_in',
  price_position_change_opt_in: 'price_position_change_opt_in',
  price_opportunity_opt_in: 'price_opportunity_opt_in',
  otb_pickup_threshold: 'otb_pickup_threshold',
  market_otb_pickup_threshold: 'market_otb_pickup_threshold',
  adr_pickup_threshold: 'adr_pickup_threshold',
  compset_change_threshold: 'compset_change_threshold',
  short_term_rental_otb_pickup_opt_in: 'short_term_rental_otb_pickup_opt_in',
  short_term_rental_otb_pickup_threshold:
    'short_term_rental_otb_pickup_threshold',
  short_term_rental_rate_pickup_opt_in: 'short_term_rental_rate_pickup_opt_in',
  short_term_rental_rate_pickup_threshold:
    'short_term_rental_rate_pickup_threshold',
} as const;

// Keys used in the alerts endpoint
export const OD_ALERTS_KEYS = {
  otb_pickup: 'otb_pickup',
  market_otb_pickup: 'market_otb_pickup',
  adr_pickup: 'adr_pickup',
  compset_change: 'compset_change',
  compset_soldout: 'compset_soldout',
  price_position_change: 'price_position_change',
  average_demand_level: 'average_demand_level',
  price_opportunity: 'price_opportunity',
  short_term_rental_otb_pickup: 'short_term_rental_otb_pickup',
  short_term_rental_rate_pickup: 'short_term_rental_rate_pickup',
};

export const OD_ALERTS_TO_TYPES_FILTER_TRANSLATION_KEY_MAPPING = {
  [OD_ALERTS_KEYS.otb_pickup]: 'overview_dashboard.my_pickup',
  [OD_ALERTS_KEYS.market_otb_pickup]: 'overview_dashboard.market_pickup',
  [OD_ALERTS_KEYS.adr_pickup]: tKey(
    'overview_dashboard.feed.alert.expand.my_adr_pickup',
  ),
  [OD_ALERTS_KEYS.compset_change]: tKey(
    'overview_dashboard.types_filter.options.competitors_change',
  ),
  [OD_ALERTS_KEYS.compset_soldout]: tKey(
    'overview_dashboard.types_filter.options.competitors_sold_out',
  ),
  [OD_ALERTS_KEYS.price_position_change]: tKey(
    'overview_dashboard.types_filter.options.price_position_change',
  ),
  [OD_ALERTS_KEYS.average_demand_level]: 'market_insight.demand_change',
  [OD_ALERTS_KEYS.price_opportunity]:
    'overview_dashboard.market.price_opportunity',
  [OD_ALERTS_KEYS.short_term_rental_otb_pickup]: tKey(
    'overview_dashboard.feed.alert.expand.short_term_rental_otb_change',
  ),
  [OD_ALERTS_KEYS.short_term_rental_rate_pickup]: tKey(
    'overview_dashboard.feed.alert.expand.short_term_rental_rate_change',
  ),
};

// Mapping of alert keys to translation keys
export const OD_ALERTS_TO_TRANSLATION_KEY_MAPPING = {
  [OD_ALERTS_KEYS.otb_pickup]: 'overview_dashboard.my_pickup',
  [OD_ALERTS_KEYS.market_otb_pickup]: 'overview_dashboard.market_pickup',
  [OD_ALERTS_KEYS.adr_pickup]: tKey(
    'overview_dashboard.feed.alert.expand.my_adr_pickup',
  ),
  [OD_ALERTS_KEYS.compset_change]: tKey(
    'overview_dashboard.feed.alert.expand.compset_change',
  ),
  [OD_ALERTS_KEYS.compset_soldout]: tKey(
    'overview_dashboard.feed.alert.expand.competitors_sold_out',
  ),
  [OD_ALERTS_KEYS.price_position_change]: tKey(
    'overview_dashboard.feed.alert.expand.price_position',
  ),
  [OD_ALERTS_KEYS.average_demand_level]: 'market_insight.demand_change',
  [OD_ALERTS_KEYS.price_opportunity]: 'market_insight.price_opportunity',
  [OD_ALERTS_KEYS.short_term_rental_otb_pickup]: tKey(
    'overview_dashboard.feed.alert.expand.short_term_rental_otb_change',
  ),
  [OD_ALERTS_KEYS.short_term_rental_rate_pickup]: tKey(
    'overview_dashboard.feed.alert.expand.short_term_rental_rate_change',
  ),
};

// Sort order to be used when displaying alerts
export const OD_ALERTS_SORT_ORDER = {
  [OD_ALERTS_KEYS.otb_pickup]: 1,
  [OD_ALERTS_KEYS.market_otb_pickup]: 2,
  [OD_ALERTS_KEYS.adr_pickup]: 3,
  [OD_ALERTS_KEYS.compset_change]: 4,
  [OD_ALERTS_KEYS.compset_soldout]: 5,
  [OD_ALERTS_KEYS.price_position_change]: 6,
  [OD_ALERTS_KEYS.price_opportunity]: 7,
  [OD_ALERTS_KEYS.average_demand_level]: 8,
  [OD_ALERTS_KEYS.short_term_rental_otb_pickup]: 9,
  [OD_ALERTS_KEYS.short_term_rental_rate_pickup]: 10,
};

export const AVAILABLE_TILES_TO_ALERT_TYPES_MAPPING = {
  [OD_SELECTED_TILES_IDS.my_otb]: [OD_ALERTS_KEYS.otb_pickup],
  [OD_SELECTED_TILES_IDS.market_otb]: [OD_ALERTS_KEYS.market_otb_pickup],
  [OD_SELECTED_TILES_IDS.demand_level]: [
    OD_ALERTS_KEYS.average_demand_level,
    OD_ALERTS_KEYS.price_opportunity,
  ],
  [OD_SELECTED_TILES_IDS.pricing_difference]: [
    OD_ALERTS_KEYS.price_position_change,
    OD_ALERTS_KEYS.compset_change,
    OD_ALERTS_KEYS.compset_soldout,
  ],
  [OD_SELECTED_TILES_IDS.short_term_rental]: [
    OD_ALERTS_KEYS.short_term_rental_otb_pickup,
    OD_ALERTS_KEYS.short_term_rental_rate_pickup,
  ],
};

// When creating a key date we need to pass the "source" to the API. So they can keep track from where in the overview keydates are created
export const OD_KEY_DATES_SOURCES = {
  alert: 'alert',
  event: 'event',
  manual: 'manual',
};

export const MAX_LENGTH_KEY_DATE_NOTE = 300;

// Keys returned by BE in meta.data_fetch_errors of alerts response in case the corresponding service fails to be fetched
export const DATA_FETCH_ERRORS = {
  occupancies: 'occupancies',
  revenue: 'revenue',
  priceposition: 'priceposition',
  compset_pricing: 'compset_pricing',
  demand: 'demand',
  events: 'events',
  short_term_rental: 'short_term_rental',
};

// Map the data fetch error keys to product names they belong to (used for translations in message to user)
export const DATA_FETCH_ERRORS_PRODUCT_NAMES = {
  occupancies: 'Rate Insight',
  revenue: 'Revenue Insight',
  priceposition: 'Rate Insight',
  compset_pricing: 'Rate Insight',
  demand: 'Market Insight',
  events: 'Rate Insight',
  short_term_rental: 'Rate Insight',
};

export const DASHBOARD_FEED_SHOW_FILTER_KEYS = {
  ALL: 'all',
  ALERTS: 'alerts',
  FAVOURITES: 'favourites',
};

export const DASHBOARD_FEED_SORT_KEYS = {
  DATE: 'date',
  NUMBER_OF_ALERTS: 'numberOfAlerts',
};

export const TOP_INSIGHT_CHART_TABS = {
  PERFORMANCE: 'performance-graph',
  RATES: 'rates-graph',
};
